// Turns whatever finix.js hands the CardTokenForm `submit` callback on failure into a
// patient-facing sentence plus a PHI-free summary for Sentry.
//
// The shape finix.js v1 actually uses (read from https://js.finix.com/v/1/finix.js and its
// iframe https://js.finix.com/v/1/payment-fields/application.js on 2026-10-13):
//   the iframe posts the tokens call's axios `e.response` as { status, data } and the parent
//   calls `cb({ status }, { status, data })`.
// So `err` is only `{ status }` — it has no `message` — and Finix's reason lives in the SECOND
// argument: `res.data._embedded.errors[].message`. The other shapes handled below are defensive.

export const FINIX_TOKENIZATION_FALLBACK_MESSAGE =
  "Card tokenization failed. Please try again.";

const REASON_PREFIX = "Card tokenization failed: ";
const MAX_REASON_LENGTH = 300;
const MAX_LIST_LENGTH = 10;
const MAX_KEYS = 20;
const MAX_SNIPPET_LENGTH = 200;
// Finix's HAL error envelope: { _embedded: { errors: [{ code, message, logref, field }] } }.
const EMBEDDED_KEY = "_embedded";

// A card-number-like run: 12–19 digits, optionally separated by single spaces or dashes.
const CARD_NUMBER_LIKE = /\d(?:[ -]?\d){11,18}/g;

export interface IFinixTokenizationSentryExtra {
  finixStatus?: number;
  finixMessages: string[];
  finixCodes: string[];
  finixLogrefs: string[];
  finixFields: string[];
  errType: string;
  errMessage?: string;
  errKeys?: string[];
  bodyType: string;
  bodyKeys?: string[];
  bodySnippet?: string;
}

export interface IFinixTokenizationErrorDetails {
  /** Patient-facing text. */
  message: string;
  /** True when `message` carries a reason from Finix (or the error) rather than the fallback. */
  hasFinixReason: boolean;
  status?: number;
  finixMessages: string[];
  codes: string[];
  logrefs: string[];
  fields: string[];
  /** Whitelisted, card-data-free summary safe to send to Sentry as `extra`. */
  sentryExtra: IFinixTokenizationSentryExtra;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const clean = (value: unknown, max = MAX_REASON_LENGTH): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.replace(CARD_NUMBER_LIKE, "[redacted]").trim();
  if (!trimmed) return undefined;
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
};

const pushUnique = (list: string[], value: unknown) => {
  const cleaned = clean(value, 100);
  if (cleaned && !list.includes(cleaned) && list.length < MAX_LIST_LENGTH) {
    list.push(cleaned);
  }
};

const parseBody = (body: unknown): unknown => {
  if (typeof body !== "string") return body;
  const trimmed = body.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return body;
  try {
    return JSON.parse(trimmed);
  } catch {
    return body;
  }
};

const describeType = (value: unknown): string => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (value instanceof Error) return "Error";
  return typeof value;
};

const keysOf = (value: unknown): string[] | undefined =>
  isObject(value) && !(value instanceof Error)
    ? Object.keys(value).sort().slice(0, MAX_KEYS)
    : undefined;

const numberOrUndefined = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

export function parseFinixTokenizationError(
  err: unknown,
  res?: unknown
): IFinixTokenizationErrorDetails {
  const errObj = isObject(err) ? err : undefined;
  const resObj = isObject(res) ? res : undefined;
  const errResponse = isObject(errObj?.response) ? errObj?.response : undefined;

  // The response body: res.data (finix.js), else err.response.data (axios error), else err.data.
  let rawBody: unknown;
  if (resObj && "data" in resObj) rawBody = resObj.data;
  else if (errResponse && "data" in errResponse) rawBody = errResponse.data;
  else if (errObj && "data" in errObj) rawBody = errObj.data;
  const body = parseBody(rawBody);
  const bodyObj = isObject(body) ? body : undefined;

  const status =
    numberOrUndefined(errObj?.status) ??
    numberOrUndefined(resObj?.status) ??
    numberOrUndefined(errResponse?.status);

  const finixMessages: string[] = [];
  const codes: string[] = [];
  const logrefs: string[] = [];
  const fields: string[] = [];

  const embedded = isObject(bodyObj?.[EMBEDDED_KEY])
    ? bodyObj?.[EMBEDDED_KEY]
    : undefined;
  const embeddedErrors: unknown[] = Array.isArray(embedded?.errors)
    ? (embedded?.errors as unknown[])
    : [];
  embeddedErrors.forEach((item: unknown) => {
    if (!isObject(item)) return;
    pushUnique(finixMessages, item.message);
    pushUnique(codes, item.code);
    pushUnique(logrefs, item.logref);
    pushUnique(fields, item.field);
  });
  if (finixMessages.length === 0) pushUnique(finixMessages, bodyObj?.message);

  // Only when Finix gave no reason: the error's own message (Error, string, { message }).
  let errMessage: string | undefined;
  if (typeof err === "string") errMessage = clean(err);
  else if (errObj) errMessage = clean(errObj.message);

  const reason =
    finixMessages.length > 0 ? clean(finixMessages.join(" ")) : errMessage;

  const sentryExtra: IFinixTokenizationSentryExtra = {
    finixStatus: status,
    finixMessages,
    finixCodes: codes,
    finixLogrefs: logrefs,
    finixFields: fields,
    errType: describeType(err),
    errKeys: keysOf(err),
    bodyType: describeType(body),
    bodyKeys: keysOf(body),
  };
  if (errMessage) sentryExtra.errMessage = errMessage;
  if (typeof body === "string") {
    sentryExtra.bodySnippet = clean(body, MAX_SNIPPET_LENGTH);
  }
  (
    Object.keys(sentryExtra) as Array<keyof IFinixTokenizationSentryExtra>
  ).forEach((key) => {
    if (sentryExtra[key] === undefined) delete sentryExtra[key];
  });

  return {
    message: reason
      ? `${REASON_PREFIX}${reason}`
      : FINIX_TOKENIZATION_FALLBACK_MESSAGE,
    hasFinixReason: !!reason,
    status,
    finixMessages,
    codes,
    logrefs,
    fields,
    sentryExtra,
  };
}

export function getFinixTokenizationErrorMessage(
  err: unknown,
  res?: unknown
): string {
  return parseFinixTokenizationError(err, res).message;
}
